import { useCallback, useEffect, useRef, useState } from 'react';
import { MapView, type MapHandle } from '@/components/game/MapView';
import { ToastLayer } from '@/components/game/ToastLayer';
import { CityPicker } from '@/components/game/CityPicker';
import { StationTypePicker } from '@/components/game/StationTypePicker';
import { VehiclePurchaseDialog } from '@/components/game/VehiclePurchaseDialog';
import { LevelUpDialog } from '@/components/game/LevelUpDialog';
import { BuildModeBadge } from '@/components/game/BuildModeBadge';
import { RadiusCircle } from '@/components/game/RadiusCircle';
import { StationMarkers } from '@/components/game/StationMarkers';
import { NpcStationMarkers } from '@/components/game/NpcStationMarkers';
import { NpcAcquireDialog } from '@/components/game/NpcAcquireDialog';
import { MissionMarkers } from '@/components/game/MissionMarkers';
import { VehicleMarkers } from '@/components/game/VehicleMarkers';
import { RouteLayers } from '@/components/game/RouteLayers';
import { HydrantLayer } from '@/components/game/HydrantLayer';
import { TopBarFloating } from '@/components/game/TopBarFloating';
import { InfoCard } from '@/components/game/InfoCard';
import { SpeedControl } from '@/components/game/SpeedControl';
import { FabCluster } from '@/components/game/FabCluster';
import { MapToolbar } from '@/components/game/MapToolbar';
import { MainPanel } from '@/components/game/panels/MainPanel';
import { DetailPanel } from '@/components/game/panels/DetailPanel';
import { MobileBuildOverlay } from '@/components/game/MobileBuildOverlay';
import { OnboardingOverlay } from '@/components/game/OnboardingOverlay';
import { RadioRequestCenter } from '@/components/game/RadioRequestCenter';
import { BetaBanner } from '@/components/beta/BetaBanner';
import { BetaOnboardingModal } from '@/components/beta/BetaOnboardingModal';
import { BugReportModal } from '@/components/beta/BugReportModal';
import { VersionPill } from '@/components/beta/VersionPill';
import { SyncIndicator } from '@/components/auth/SyncIndicator';
import { scheduleCloudSync } from '@/state/cloudSync';
import { preloadSounds } from '@/lib/audio';
import { Confetti } from '@/components/ui/Confetti';
import { useGameClock } from '@/hooks/useGameClock';
import { useVehicleRouting } from '@/hooks/useVehicleRouting';
import { useHydrantsLoader } from '@/hooks/useHydrantsLoader';
import { useNpcStationsLoader } from '@/hooks/useNpcStationsLoader';
import { useWaterFeaturesLoader } from '@/hooks/useWaterFeaturesLoader';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useViewport, VIEWPORT_BREAKPOINTS } from '@/hooks/useViewport';
import { registerMapHandle } from '@/hooks/mapHandleRegistry';
import { useGameStore } from '@/state/gameStore';
import {
  STATION_SPECS,
  defaultStationName,
  getCurrentTier,
} from '@/data/stations';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { validateStationPlacement } from '@/lib/stationValidation';
import { formatBuildTime } from '@/data/buildTime';
import { formatEuro } from '@/lib/format';
import type { NpcStation, Station, StationKind, VehicleType } from '@/state/types';

export default function Game() {
  useGameClock();
  useVehicleRouting();
  useHydrantsLoader();
  useNpcStationsLoader();
  useWaterFeaturesLoader();
  useKeyboardShortcuts();

  const homeCity = useGameStore((s) => s.homeCity);
  const level = useGameStore((s) => s.level);
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const stations = useGameStore((s) => s.stations);
  const buildMode = useGameStore((s) => s.buildMode);
  const setBuildMode = useGameStore((s) => s.setBuildMode);
  const pushToast = useGameStore((s) => s.pushToast);
  const buyLevelUp = useGameStore((s) => s.buyLevelUp);
  const buildStation = useGameStore((s) => s.buildStation);
  const buyVehicle = useGameStore((s) => s.buyVehicle);
  const consumeMigrationToast = useGameStore((s) => s.consumeMigrationToast);

  const mainPanelOpen = useGameStore((s) => s.mainPanelOpen);
  const closeMainPanel = useGameStore((s) => s.closeMainPanel);
  const selectedDetail = useGameStore((s) => s.selectedDetail);
  const clearDetail = useGameStore((s) => s.clearDetail);

  const viewport = useViewport();
  const isMobile = viewport === 'mobile';

  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [buyVehicleFor, setBuyVehicleFor] = useState<Station | null>(null);
  const [levelUpOpen, setLevelUpOpen] = useState(false);
  const [confettiKey, setConfettiKey] = useState<number | null>(null);
  const [acquireNpc, setAcquireNpc] = useState<NpcStation | null>(null);

  // Phase 15.2/15.3 — Beta-UI-State
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [betaOnboardingOpen, setBetaOnboardingOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (!__IS_BETA__) return false;
    try {
      return window.localStorage.getItem('betaOnboardingShown') !== '1';
    } catch {
      return false;
    }
  });
  const ackBetaOnboarding = useCallback(() => {
    setBetaOnboardingOpen(false);
    try {
      window.localStorage.setItem('betaOnboardingShown', '1');
    } catch {
      // ignore
    }
  }, []);

  const mapRef = useRef<MapHandle>(null);

  // Phase 7.4.5 — register the map handle so any consumer can flyTo without props.
  useEffect(() => {
    registerMapHandle(mapRef.current);
    return () => registerMapHandle(null);
  });

  // Phase 12.2.5 — Pre-load sound files once on mount so the first siren
  // doesn't lag.
  useEffect(() => {
    preloadSounds();
  }, []);

  // Phase 16.4.3 — Snapshot-basiertes Auto-Sync. Wir können nicht auf jedem
  // gameStore-Change syncen (gameTime tickt 10 Hz; das würde den Debounce
  // permanent zurücksetzen). Stattdessen vergleichen wir einen Hash der
  // sync-relevanten Felder und triggern nur bei echten Änderungen.
  useEffect(() => {
    let prevHash = '';
    const unsub = useGameStore.subscribe((state) => {
      const hash = JSON.stringify({
        credits: state.credits,
        points: state.points,
        level: state.level,
        activeSlotId: state.activeSlotId,
        stations: state.stations.map((s) => `${s.id}:${s.level}:${s.totalSpent}`).join('|'),
        vehicles: state.vehicles.length,
        missions: state.missions.length,
        homeCity: state.homeCity?.displayName ?? '',
        mode: state.mode,
      });
      if (hash !== prevHash) {
        prevHash = hash;
        scheduleCloudSync();
      }
    });
    return unsub;
  }, []);

  // One-shot migration toast.
  useEffect(() => {
    const text = consumeMigrationToast();
    if (text) {
      pushToast({ kind: 'info', text });
    }
  }, [consumeMigrationToast, pushToast]);

  // Cross-panel rule.
  useEffect(() => {
    if (!selectedDetail) return;
    if (window.innerWidth < VIEWPORT_BREAKPOINTS.TABLET_MAX && mainPanelOpen) {
      closeMainPanel();
    }
  }, [selectedDetail, mainPanelOpen, closeMainPanel]);

  // ESC priority chain.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (typePickerOpen || buyVehicleFor !== null || levelUpOpen) return;

      if (buildMode) {
        setBuildMode(null);
        return;
      }
      if (selectedDetail) {
        clearDetail();
        return;
      }
      if (mainPanelOpen) {
        closeMainPanel();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    buildMode,
    setBuildMode,
    selectedDetail,
    clearDetail,
    mainPanelOpen,
    closeMainPanel,
    typePickerOpen,
    buyVehicleFor,
    levelUpOpen,
  ]);

  /**
   * Build placement — desktop/tablet uses tap-to-place, mobile uses center-confirm.
   * The shared core is `placeAt(lngLat)` so both paths reuse the same validation.
   */
  const placeAt = useCallback(
    (lngLat: { lng: number; lat: number }) => {
      if (!buildMode || !homeCity) return;
      const { stationKind } = buildMode;
      const spec = STATION_SPECS[stationKind];
      const tier = getCurrentTier(stationKind, 1);

      const result = validateStationPlacement({
        candidate: lngLat,
        kind: stationKind,
        stations,
        homeCityCenter: homeCity.center,
        level,
      });

      if (!result.ok) {
        pushToast({ kind: 'error', text: result.message });
        return;
      }

      const sameKindCount = stations.filter((s) => s.kind === stationKind).length + 1;
      const station = buildStation({
        kind: stationKind,
        name: defaultStationName(stationKind, sameKindCount),
        position: lngLat,
      });
      if (!station) {
        pushToast({ kind: 'error', text: 'Bau fehlgeschlagen – Credits fehlen.' });
        return;
      }

      const isInstant = mode === 'sandbox' || tier.buildTimeHours === 0;
      const costSuffix = tier.cost > 0 ? ` (${formatEuro(tier.cost)})` : '';
      pushToast({
        kind: 'success',
        text: isInstant
          ? `${spec.label} „${station.name}" gebaut${costSuffix}.`
          : `${spec.label} „${station.name}" wird gebaut – fertig in ${formatBuildTime(tier.buildTimeHours, mode)}${costSuffix}.`,
      });
      setBuildMode(null);
    },
    [buildMode, stations, homeCity, level, mode, pushToast, buildStation, setBuildMode],
  );

  // Desktop/tablet: tap on map → build at tap location.
  const handleMapClick = useCallback(
    (lngLat: { lng: number; lat: number }) => {
      if (isMobile) return; // Mobile uses confirm-button instead
      placeAt(lngLat);
    },
    [isMobile, placeAt],
  );

  // Mobile: confirm-button → build at current map center.
  const handleMobileConfirm = useCallback(() => {
    const center = mapRef.current?.getCenter();
    if (!center) return;
    placeAt(center);
  }, [placeAt]);

  // City-picker path. Beta-Onboarding zeigt sich VOR dem CityPicker.
  if (!homeCity) {
    return (
      <>
        <BetaBanner onReport={() => setBugReportOpen(true)} />
        <CityPicker />
        <BetaOnboardingModal open={betaOnboardingOpen} onAcknowledge={ackBetaOnboarding} />
        <BugReportModal open={bugReportOpen} onClose={() => setBugReportOpen(false)} />
        <VersionPill />
      </>
    );
  }

  const handleStartBuild = () => {
    setTypePickerOpen(true);
  };

  const handleTypeChosen = (kind: StationKind) => {
    const spec = STATION_SPECS[kind];
    const tier = getCurrentTier(kind, 1);
    if (mode !== 'sandbox' && credits < tier.cost) {
      pushToast({ kind: 'error', text: `Nicht genug Credits für ${spec.label}.` });
      return;
    }
    setTypePickerOpen(false);
    closeMainPanel();
    setBuildMode({ kind: 'station', stationKind: kind });
    pushToast({
      kind: 'info',
      text: isMobile
        ? `Platziere ${spec.label} – Karte verschieben und „Hier bauen" tippen.`
        : `Platziere ${spec.label} – klicke in die Karte.`,
    });
  };

  const handleBuyVehicle = (
    station: Station,
    type: VehicleType,
    opts?: {
      name?: string;
      callsign?: string;
      used?: boolean;
      usedKilometers?: number;
      extraModuleIds?: string[];
      removedDefaultFamilies?: string[];
    },
  ) => {
    const spec = VEHICLE_SPECS[type];
    const cost = Math.round(spec.cost * (opts?.used ? 0.6 : 1));
    if (mode !== 'sandbox' && credits < cost) {
      pushToast({ kind: 'error', text: `Nicht genug Credits für ${spec.label}.` });
      return;
    }
    const vehicle = buyVehicle(station.id, type, opts);
    if (!vehicle) {
      pushToast({ kind: 'error', text: 'Kauf fehlgeschlagen.' });
      return;
    }
    const usedSuffix = opts?.used ? ' (gebraucht)' : '';
    pushToast({
      kind: 'success',
      text: `${opts?.name ?? spec.label}${usedSuffix} für „${station.name}" gekauft (${formatEuro(cost)}).`,
    });
  };

  const handleConfirmLevelUp = () => {
    const ok = buyLevelUp();
    if (ok) {
      pushToast({ kind: 'success', text: `Level ${level + 1} erreicht – Einsatzgebiet wächst.` });
      setConfettiKey(Date.now());
    } else {
      pushToast({ kind: 'error', text: 'Level-Up fehlgeschlagen.' });
    }
    setLevelUpOpen(false);
  };

  return (
    <main className="relative h-screen w-screen overflow-hidden bg-bg-primary text-fg-primary">
      <BetaBanner onReport={() => setBugReportOpen(true)} />
      <MapView ref={mapRef} onMapClick={handleMapClick} crosshair={buildMode !== null && !isMobile}>
        <RadiusCircle />
        <RouteLayers />
        <HydrantLayer />
        <StationMarkers />
        <NpcStationMarkers onSelect={setAcquireNpc} />
        <MissionMarkers />
        <VehicleMarkers />
      </MapView>

      <InfoCard />
      <TopBarFloating onOpenLevelUp={() => setLevelUpOpen(true)} />
      <SpeedControl />
      <MapToolbar />
      <FabCluster />
      <VersionPill />
      <SyncIndicator />
      <BugReportModal open={bugReportOpen} onClose={() => setBugReportOpen(false)} />

      {!isMobile && <BuildModeBadge buildMode={buildMode} onCancel={() => setBuildMode(null)} />}
      {isMobile && (
        <MobileBuildOverlay
          buildMode={buildMode}
          onConfirm={handleMobileConfirm}
          onCancel={() => setBuildMode(null)}
        />
      )}

      <MainPanel onRequestBuildStation={handleStartBuild} />
      <DetailPanel onRequestBuyVehicle={(s) => setBuyVehicleFor(s)} />

      <StationTypePicker
        open={typePickerOpen}
        credits={credits}
        onClose={() => setTypePickerOpen(false)}
        onSelect={handleTypeChosen}
      />

      <VehiclePurchaseDialog
        station={buyVehicleFor}
        credits={credits}
        onClose={() => setBuyVehicleFor(null)}
        onBuy={(st, type, opts) => {
          handleBuyVehicle(st, type, opts);
        }}
      />

      <LevelUpDialog
        open={levelUpOpen}
        level={level}
        credits={credits}
        isSandbox={mode === 'sandbox'}
        onClose={() => setLevelUpOpen(false)}
        onConfirm={handleConfirmLevelUp}
      />

      <NpcAcquireDialog npc={acquireNpc} onClose={() => setAcquireNpc(null)} />

      <Confetti trigger={confettiKey} />
      <OnboardingOverlay />
      <RadioRequestCenter />
      <ToastLayer />
    </main>
  );
}
